import { useSetAtom } from "jotai";
import { useCallback } from "react";

import { layoutAtom } from "@/core/state";
import { listAllPanelInstances, setPanelParams } from "@/shell/layout";

/**
 * Persiste un bout d'etat UI d'un panneau (ex: onglet interne actif) dans les
 * `params` de son instance de layout — donc dans le workspace persiste
 * (localStorage). Survit au reload, et chaque instance ouverte du meme panel
 * garde sa propre valeur (ex: 2 creator-detail ouverts en parallele, chacun
 * sur un onglet different).
 *
 * Pourquoi : avant, des panneaux comme CreatorDetailView gardaient leur onglet
 * actif en `useState` local. Au Ctrl+R, le panneau etait bien restaure (scope
 * via `params.creatorSlug`) mais l'onglet retombait sur sa valeur par defaut.
 * Bug rapporte par l'user : "j'etais sur Miami Flow, je fais Ctrl+R, ca me
 * remet sur Profil".
 *
 * Usage (dans un composant de panel qui recoit `instanceId` + `params`) :
 *
 *   const [tab, setTab] = usePanelInstanceState<TabId>(
 *     instanceId, params, "tab", "profile"
 *   );
 *
 * @param instanceId  id de l'instance (PanelViewProps.instanceId)
 * @param params      params actuels de l'instance (PanelViewProps.params)
 * @param key         cle sous laquelle stocker la valeur dans params
 * @param fallback    valeur par defaut si params[key] est absent
 */
export function usePanelInstanceState<T extends string>(
  instanceId: string | undefined,
  params: Record<string, unknown> | undefined,
  key: string,
  fallback: T
): [T, (value: T) => void] {
  const setLayout = useSetAtom(layoutAtom);

  const value = (params?.[key] as T | undefined) ?? fallback;

  const setValue = useCallback(
    (next: T) => {
      if (!instanceId) return;
      setLayout((prev) => {
        // Relit les params frais depuis `prev` pour ne pas ecraser une autre
        // mise a jour concurrente d'un champ different.
        const inst = listAllPanelInstances(prev).find(
          (p) => p.id === instanceId
        );
        const currentParams =
          (inst?.params as Record<string, unknown> | undefined) ?? {};
        if (currentParams[key] === next) return prev;
        return setPanelParams(prev, instanceId, {
          ...currentParams,
          [key]: next,
        });
      });
    },
    [instanceId, key, setLayout]
  );

  return [value, setValue];
}
